/**
 * Limits & Fees Articles — Russian
 */

import type { HelpArticle } from "../../../types";

export const LIMITS_FEES_ARTICLES_RU: HelpArticle[] = [
  {
    id: "lf-001",
    slug: "transaction-limits-overview",
    title: "Обзор лимитов",
    description: "Какие лимиты действуют на аккаунте",
    category: "limits-fees",
    popular: true,
    order: 1,
    keywords: ["лимиты", "максимум", "минимум", "ограничения"],
    content: `
# Обзор лимитов

Лимиты зависят от уровня верификации и истории аккаунта.

## Категории лимитов

### Лимиты на депозиты
- Минимум: $10 за транзакцию
- Максимум: зависит от уровня

### Лимиты на выплаты
- Лимит за транзакцию
- Дневной лимит
- Месячный лимит

### Лимиты на конвертацию
- Лимит за конвертацию
- Дневной объём
- Месячный объём

## Лимиты по уровням

### Стандартный
| Тип лимита | Сумма |
|------------|-------|
| За депозит | $25 000 |
| За выплату | $10 000 |
| Выплаты в день | $25 000 |
| Объём в месяц | $100 000 |

### Расширенный
| Тип лимита | Сумма |
|------------|-------|
| За депозит | $100 000 |
| За выплату | $50 000 |
| Выплаты в день | $100 000 |
| Объём в месяц | $500 000 |

### Корпоративный
| Тип лимита | Сумма |
|------------|-------|
| За депозит | Индивидуально |
| За выплату | Индивидуально |
| Выплаты в день | Индивидуально |
| Объём в месяц | Индивидуально |

## Просмотр лимитов

1. Откройте Настройки > Лимиты
2. Посмотрите текущий уровень
3. Проверьте оставшиеся лимиты
4. Отслеживайте использование

## Когда сбрасываются лимиты

- **Дневные**: в полночь UTC
- **Месячные**: первого числа месяца
- **За транзакцию**: сразу после завершения

## Временные ограничения

Дополнительные лимиты могут действовать для:
- Новых аккаунтов (первые 30 дней)
- Первых крупных транзакций
- После изменений в аккаунте
- При проверках безопасности
    `.trim(),
  },
  {
    id: "lf-002",
    slug: "increasing-your-limits",
    title: "Повышение лимитов",
    description: "Как получить более высокие лимиты",
    category: "limits-fees",
    order: 2,
    keywords: ["повысить", "увеличить", "апгрейд", "больше"],
    content: `
# Повышение лимитов

Готовы к более высоким лимитам? Вот как это сделать.

## Пути повышения

### Стандартный → Расширенный

**Требования:**
- Аккаунт старше 30 дней
- Объём операций от $5 000
- Без нарушений комплаенса
- Дополнительная верификация

**Шаги:**
1. Откройте Настройки > Верификация
2. Нажмите «Повысить до Расширенного»
3. Предоставьте подтверждение адреса
4. Укажите источник средств
5. Дождитесь проверки (1–3 дня)

### Расширенный → Корпоративный

**Требования:**
- Бизнес-аккаунт
- Большой объём транзакций
- Индивидуальная проверка
- Специальное соглашение

**Шаги:**
1. Напишите на enterprise@supabank.xyz
2. Запланируйте консультацию
3. Пройдите бизнес-верификацию
4. Согласуйте условия
5. Апгрейд аккаунта

## Необходимые документы

### Для Расширенного
- Подтверждение адреса (свежий счёт за коммуналку)
- Банковская выписка (за 3 месяца)
- Декларация об источнике средств

### Для Корпоративного
- Регистрационные документы компании
- Финансовая отчётность
- Решение совета директоров (если применимо)
- Верификация бенефициаров

## Сроки одобрения

| Апгрейд | Обычный срок |
|---------|--------------|
| Стандартный → Расширенный | 1–3 рабочих дня |
| Расширенный → Корпоративный | 5–10 рабочих дней |

## Советы для одобрения

Документы должны быть чёткими и читаемыми
Имена должны совпадать
Документы свежие (< 3 месяцев)
Заполните все поля правильно
Отвечайте на запросы быстро
    `.trim(),
  },
  {
    id: "lf-003",
    slug: "fee-structure",
    title: "Структура комиссий",
    description: "Полная разбивка всех комиссий",
    category: "limits-fees",
    order: 3,
    keywords: ["комиссии", "стоимость", "тарифы", "цены"],
    content: `
# Структура комиссий

Прозрачные тарифы без скрытых комиссий.

## Комиссии за конвертацию

При конвертации крипты в фиат применяются две комиссии:

1. **Комиссия платформы**: 2% от суммы
2. **Комиссия Bridge**: ~0.1% от суммы

**Общая комиссия: ~2.1%**

### Тарифы по объёму

| Месячный объём | Комиссия платформы | Итого (примерно) |
|----------------|-------------------|------------------|
| Стандартный | 2.0% | ~2.1% |
| Большой объём | 2.0% | ~2.1% |
| Корпоративный | Индивидуально | Индивидуально |

*Комиссия Bridge может незначительно меняться в зависимости от рынка*

## Комиссии за выплату

| Способ | Комиссия |
|--------|----------|
| ACH (США) | Бесплатно |
| Wire (внутренний США) | $15 |
| Wire (международный) | $25 |
| SEPA (Европа) | Бесплатно |
| SPEI (Мексика) | Бесплатно |

## Без скрытых комиссий

**Мы не берём плату за:**
- Создание аккаунта
- Обслуживание аккаунта
- Получение депозитов
- Отображение валют
- Поддержку клиентов
- API-доступ (бизнес)

## Сторонние комиссии

**Ваши расходы вне Supabank:**
- Комиссии блокчейна (газ)
- Комиссии вашего банка за входящие переводы
- Комиссии банков-посредников (международные wire)
- Конвертация валюты в вашем банке

## Пример расчёта комиссий

\`\`\`
Депозит: 1 000 USDC
└── Сетевая комиссия: ~$2 (вы платите блокчейну)

Конвертация: 1 000 USDC → ~979 USD
└── Комиссия платформы: $20,00 (2%)
└── Комиссия Bridge: ~$1,00 (0.1%)
└── Всего за конвертацию: ~$21,00

Выплата: ~$979 через ACH
└── Комиссия выплаты: $0 (ACH бесплатно)

═══════════════════════
Итого получите: ~$979,00
\`\`\`

## Просмотр комиссий

1. Детали транзакции показывают все комиссии
2. Месячная сводка в настройках
3. Экспорт включает разбивку комиссий
    `.trim(),
  },
  {
    id: "lf-004",
    slug: "volume-based-pricing",
    title: "Тарифы по объёму",
    description: "Скидки для крупных клиентов",
    category: "limits-fees",
    order: 4,
    keywords: ["объём", "скидка", "тарифы", "уровень", "экономия"],
    content: `
# Тарифы по объёму

Больше объём — ниже комиссии.

## Уровни по объёму

| Месячный объём | Комиссия платформы | Итого (примерно) |
|----------------|-------------------|------------------|
| До $100 000 | 2.0% | ~2.1% |
| $100 001 – $500 000 | 2.0% | ~2.1% |
| $500 001 – $1 000 000 | 2.0% | ~2.1% |
| $1 000 000+ | Индивидуально | Индивидуально |

*Примечание: Комиссия платформы фиксирована на 2%. Комиссия Bridge (~0.1%) отдельная и может меняться.*

## Как считается объём

- Скользящий 30-дневный период
- Все конвертации включены
- Обновляется ежедневно
- Автоматическое присвоение уровня

## Преимущества уровней

### Стандартный (< $100K)
- 2.0% комиссия платформы (~2.1% всего)
- Стандартные лимиты
- Email-поддержка
- Обычная обработка

### Большой объём ($100K – $1M)
- 2.0% комиссия платформы (~2.1% всего)
- Повышенные лимиты
- Приоритетная поддержка
- Ускоренная обработка

### Корпоративный ($1M+)
- Индивидуальные ставки
- Без ограничений по лимитам
- Персональный менеджер
- VIP-обслуживание
- Приоритет API

## Как достичь высокого уровня

1. Увеличьте объём транзакций
2. Поддерживайте регулярную активность
3. Без нарушений комплаенса
4. Автоматический апгрейд

## Отслеживание объёма

Смотрите свой объём:
1. Откройте Настройки > Лимиты
2. Смотрите «30-дневный объём»
3. Следите за прогрессом по уровню
4. Ориентировочная смена уровня

## Корпоративные тарифы

Для объёмов от $1M/месяц:
- Напишите на enterprise@supabank.xyz
- Индивидуальные ставки
- Обязательства по объёму
- SLA-соглашения
    `.trim(),
  },
  {
    id: "lf-005",
    slug: "enterprise-limits",
    title: "Корпоративные лимиты",
    description: "Лимиты для бизнеса с большими объёмами",
    category: "limits-fees",
    order: 5,
    keywords: ["корпоративный", "бизнес", "без ограничений", "индивидуально"],
    content: `
# Корпоративные лимиты

Индивидуальные лимиты для бизнеса с большими объёмами.

## Возможности корпоративного уровня

### Индивидуальные лимиты
- Лимиты под ваши потребности
- Без стандартных ограничений
- Корректировки по объёму
- Гибкие условия

### Выделенная поддержка
- Персональный менеджер
- Прямая телефонная линия
- Приоритетная обработка обращений
- Техническая поддержка интеграции

### Расширенный SLA
- Гарантия доступности 99.9%
- Приоритетная обработка
- Ускоренные расчёты
- Время реагирования на инциденты

## Как получить корпоративный статус

### Минимальные требования
- Месячный объём: от $1 000 000
- Завершённая бизнес-верификация
- Чистая история комплаенса
- Стабильная активность

### Процесс подачи
1. Напишите на enterprise@supabank.xyz
2. Первичная консультация
3. Проверка документов компании
4. Оценка рисков
5. Согласование условий
6. Апгрейд аккаунта

## Корпоративные тарифы

| Услуга | Корпоративная ставка |
|--------|---------------------|
| Конвертация | от 0.15% |
| Wire-выплаты | Снижены или отменены |
| API-запросы | Без ограничений |
| Поддержка | Премиум включена |

*Итоговые ставки зависят от объёма и условий*

## Варианты интеграции

### API-доступ
- Полный доступ к API
- Тестовая среда
- Техническая документация
- Поддержка интеграции

### Индивидуальные решения
- Варианты White Label
- Индивидуальные отчёты
- Выделенные эндпоинты
- Массовые операции

## Как начать

Готовы к корпоративному уровню? Напишите:
- Email: enterprise@supabank.xyz
- Укажите: название компании, ожидаемый объём, сценарий использования
    `.trim(),
  },
];
